<template>
  <Popover v-bind="$attrs" @show="beforeShow">
    <slot />
    <template #popover>
      <div class="mini-calendar" @click.stop.prevent>
        <LoadingIndicator v-if="busy" />
        <div class="pager">
          <button class="arrow prev" :disabled="!showLeftArrow" @click="setByMonth(currentMonth - 1)">前月</button>
          <p class="selected-date">
            <span>{{ currentYear }}年</span>
            <span class="month">{{ currentMonth }}月</span>
          </p>
          <button class="arrow next" :disabled="!showRightArrow" @click="setByMonth(currentMonth + 1)">翌月</button>
        </div>
        <table>
          <thead>
            <tr>
              <th>月</th>
              <th>火</th>
              <th>水</th>
              <th>木</th>
              <th>金</th>
              <th>土</th>
              <th>日</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(days, rowIdx) in calendar" :key="rowIdx">
              <td
                v-for="(day, colIdx) in days"
                v-close-popover="day && day.day && !day.isDisabled"
                :class="{
                  'another-month': !day || !day.day,
                  'first-day': day.day === 1,
                  'national-holiday': day.isNationalHoliday,
                  'is-disabled': day.isDisabled || day.isBusinessday,
                  'is-selected': day.isSelected,
                }"
                :key="`day-${colIdx}-${day.day}`"
                @click="setByDay(day)"
              >
                <div class="date">
                  {{ day.day }}
                </div>
                <template v-if="day.day">
                  <div v-if="day.lunch" class="count lunch">
                    <SunIcon width="8" height="8" />
                    <span>{{ day.lunch }}</span>
                  </div>
                  <div v-if="day.dinner" class="count dinner">
                    <MoonIcon width="8" height="8" />
                    <span>{{ day.dinner }}</span>
                  </div>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-show="(!isToday && todayButton) || clearable" class="footer">
          <!-- TODO: レンダリング時にcomputedの結果が取得できないためディレクティブの実行がされないのでv-show -->
          <Button v-show="!isToday && todayButton" v-close-popover x-small @click="setToday">本日</Button>
          <Button v-if="clearable" v-close-popover small fit class="clearable" @click="clear">日付指定しない</Button>
        </div>
      </div>
    </template>
  </Popover>
</template>

<script>
import { chunk } from 'lodash'
import { addDays, format } from 'date-fns'
import { formatDate, toHourClock, today } from '@/utils/date'
import { padZero, formatExceed } from '@/utils/number'
import MoonIcon from '@/components/icons/moon'
import SunIcon from '@/components/icons/sun'
import Popover, { ClosePopover } from '@/components/molecules/Popover'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import Button from '@/components/atoms/Button'

/**
 * 日付選択
 */
export default {
  name: 'CalendarPopover',
  components: {
    MoonIcon,
    SunIcon,
    Popover,
    LoadingIndicator,
    Button,
  },
  directives: {
    ClosePopover,
  },
  props: {
    /** 選択済みの日付 */
    date: String,
    /** 表示範囲（開始日） */
    from: { type: String, default: '2000-01-01' },
    /** 表示範囲（終了日） */
    to: { type: String, default: '2099-12-31' },
    /** 選択可能な範囲（開始日） */
    minDate: { type: String, default: '2000-01-01' },
    /** 選択可能な範囲（終了日） */
    maxDate: { type: String, default: '2099-12-31' },
    /** 祝日情報 */
    holidayList: { type: Array },
    /** 定休日情報 */
    businessDay: { type: Object, default: () => {} },
    /** 本日ボタン有無 */
    todayButton: { type: Boolean, default: false },
    /** クリアボタン */
    clearable: { type: Boolean, default: false },
    /** 予約数一覧 */
    reserveCalendar: { type: Object, default: () => ({}) },
    /** ローディング */
    busy: { type: Boolean, default: false },
    /** SP */
    isSP: { type: Boolean, default: false },
  },
  data() {
    return {
      currentDay: '',
      currentMonth: '',
      currentYear: '',
      selectedDay: '',
      selectedMonth: '',
      selectedYear: '',
    }
  },
  computed: {
    calendar() {
      const days = []

      // 月初めの曜日を取得（0=日曜...6=土曜）
      const startOfMonthDay = new Date(this.currentYear, this.currentMonth - 1, 0).getDay()
      for (let i = 0, len = startOfMonthDay; i < len; i++) {
        days.push('')
      }

      // 最終日を取得
      const daysInMonth = 32 - new Date(this.currentYear, this.currentMonth - 1, 32).getDate()
      for (let i = 0; i < daysInMonth; i++) {
        days.push(i + 1)
      }

      const currentYearMonth = `${this.currentYear}-${padZero(this.currentMonth)}`

      const dayObjects = days.map(day => {
        const date = formatDate(`${this.currentYear}-${this.currentMonth}-${day}`)

        // 祝日判定
        const isNationalHoliday = this.holidayList && this.holidayList.indexOf(date) > -1

        // 非活性判定
        const isDisabled = this.isLimitMinDate(day) || this.isLimitMaxDate(day)

        // 選択中判定
        const isSelected =
          this.currentDay === day && this.currentMonth === this.selectedMonth && this.currentYear === this.selectedYear

        // 営業日判定
        const isBusinessday =
          this.businessDay && currentYearMonth in this.businessDay && !this.businessDay[currentYearMonth].includes(day)

        // 予約数
        const lunch = this.reserveCalendar[date]?.lunch?.groups
        const dinner = this.reserveCalendar[date]?.dinner?.groups
        const prefix = num => (this.isSP && String(num).length > 2 ? '' : '組')

        return {
          day,
          isNationalHoliday,
          isDisabled,
          isSelected,
          isBusinessday,
          lunch: lunch ? `${formatExceed(lunch, 999, '')}${prefix(lunch)}` : '',
          dinner: dinner ? `${formatExceed(dinner, 999, '')}${prefix(dinner)}` : '',
        }
      })

      const num = 7
      const calendarObjects = chunk(dayObjects, num)
      const chunkLen = calendarObjects.length - 1
      for (let i = 0, len = num - calendarObjects[chunkLen].length; i < len; i++) {
        calendarObjects[chunkLen].push('')
      }

      return calendarObjects
    },
    showLeftArrow() {
      return this.from && format(this.from, 'YYYYMM') < `${this.currentYear}${padZero(this.currentMonth)}`
    },
    showRightArrow() {
      return this.to && `${this.currentYear}${padZero(this.currentMonth)}` < format(this.to, 'YYYYMM')
    },
    isToday() {
      return this.date === today()
    },
  },
  watch: {
    date() {
      this.initDate()
    },
  },
  created() {
    this.initDate()
  },
  methods: {
    formatDate,
    today,
    beforeShow() {
      this.$emit('beforeShow')
    },
    prev() {
      this.changeDate(formatDate(addDays(this.date, -1), 'YYYY-MM-DD'))
    },
    next() {
      this.changeDate(formatDate(addDays(this.date, 1), 'YYYY-MM-DD'))
    },
    initDate() {
      // 選択した日付を設定
      this.selectedYear = Number(formatDate(this.date, 'YYYY'))
      this.selectedMonth = Number(formatDate(this.date, 'M'))
      this.selectedDay = Number(formatDate(this.date, 'D'))

      const _today = toHourClock(new Date())
      this.currentYear = Number(this.selectedYear || _today.year)
      this.currentMonth = Number(this.selectedMonth || _today.month)
      this.currentDay = Number(this.selectedDay || _today.day)
    },
    setByMonth(month) {
      this.currentMonth = month
      if (month === 13) {
        this.currentYear = this.currentYear + 1
        this.currentMonth = 1
      }
      if (month === 0) {
        this.currentYear = this.currentYear - 1
        this.currentMonth = 12
      }
      this.$emit('setDate', `${this.currentYear}-${padZero(this.currentMonth)}-01`)
    },
    setByDay(dateObj) {
      if (!dateObj.day || this.isLimitMinDate(dateObj.day) || this.isLimitMaxDate(dateObj.day)) {
        return
      }

      this.selectedYear = this.currentYear
      this.selectedMonth = this.currentMonth
      this.selectedDay = dateObj.day

      const formatDateObj = formatDate(`${this.selectedYear}-${this.selectedMonth}-${this.selectedDay}`)

      this.$emit('change', formatDateObj)
    },
    isLimitMinDate(day) {
      return day && this.minDate
        ? `${this.currentYear}-${padZero(this.currentMonth)}-${padZero(day)}` < this.minDate
        : false
    },
    isLimitMaxDate(day) {
      return day && this.maxDate
        ? this.maxDate < `${this.currentYear}-${padZero(this.currentMonth)}-${padZero(day)}`
        : false
    },
    setToday() {
      this.$emit('change', today())
    },
    clear() {
      this.$emit('change', null)
    },
  },
}
</script>

<style lang="postcss" scoped>
td.is-disabled {
  background-color: var(--very-light-gray-4);
  cursor: default;
}

.footer {
  display: flex;
  justify-content: center;

  .clearable {
    padding: 0 16px;
  }
}

.mini-calendar {
  align-content: flex-start;
  box-sizing: border-box;
  display: grid;
  grid-gap: 16px;
  padding: 16px;
  width: 452px;

  .pager {
    align-items: center;
    display: flex;
    height: 24px;
    justify-content: space-between;
    margin: 0 auto;
    position: relative;
    width: 256px;

    .arrow {
      appearance: none;
      background-color: var(--white);
      border: 0;
      display: block;
      height: 24px;
      outline: 0;
      position: relative;
      text-indent: -9999px;
      width: 24px;

      &::before {
        border-right: 2px solid var(--moderate-blue);
        border-top: 2px solid var(--moderate-blue);
        bottom: 0;
        content: '';
        height: 7px;
        left: 9px;
        margin: auto;
        position: absolute;
        top: 0;
        width: 7px;
      }

      &.prev::before {
        transform: rotate(-135deg);
      }

      &.next::before {
        transform: rotate(45deg);
      }

      &:disabled::before {
        border-right: 2px solid var(--dark-gray);
        border-top: 2px solid var(--dark-gray);
        pointer-events: none;
      }
    }

    .selected-date {
      align-items: center;
      color: var(--very-dark-gray-2);
      display: flex;
      height: 24px;
      width: 127px;

      span {
        display: inline-block;
      }

      .month {
        box-sizing: border-box;
        font-size: 21px;
        font-weight: bold;
        position: relative;
        text-align: right;
        width: 67px;
      }
    }
  }

  table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;

    tr {
      display: grid;
      grid-auto-flow: column;
    }

    th {
      background-color: var(--very-dark-gray);
      color: var(--white);
      font-size: 11px;
      font-weight: normal;
      height: 16px;
      text-align: center;
      vertical-align: middle;
    }

    td {
      border-bottom: 1px solid var(--border-color);
      border-right: 1px solid var(--border-color);
      box-sizing: border-box;
      cursor: pointer;
      display: flex;
      flex-flow: column;
      font-size: 21px;
      height: 63px;
      justify-content: space-between;
      line-height: 1;
      padding: 8px 10px;
      text-align: center;
      width: 60px;

      &.another-month {
        border-right: 0;
        cursor: default;
      }

      &.first-day {
        border-left: 1px solid var(--border-color);
      }

      &:first-child {
        border-left: 0;
      }

      &:last-child {
        border-right: 0;
      }

      &:nth-child(6) {
        color: var(--moderate-blue);
      }

      &:nth-child(7),
      &.national-holiday {
        color: var(--vivid-pink);
      }

      &.is-disabled {
        background-color: var(--very-light-gray-4);
      }

      &.is-selected {
        background-color: var(--very-light-yellow);
      }

      .count {
        align-items: center;
        color: var(--dark-gray);
        display: flex;
        font-size: 10px;
        justify-content: space-between;
        line-height: 1;

        svg {
          fill: var(--dark-gray);
        }
      }

      .lunch {
        margin: 4px 0 auto;
      }
    }

    tr:last-child {
      td {
        border-bottom: 0;
      }
    }
  }

  button {
    font-family: inherit;
  }

  .responsive & {
    @media screen and (--sp-size) {
      min-height: 0;
      padding: 16px 0;
      width: 100%;

      td {
        height: 65px;
        padding: 8px;
        width: 50px;
      }
    }
  }
}
</style>
